/**
 * Founder Morning Digest — GC60 Priority 4.
 *
 * The primary Founder operational management surface. Reviewed first thing
 * each working day. Renders the ten Founder-specified digest sections and
 * the First Light Ledger. Every user-visible string comes from the backend
 * narrator (single voice — no second interaction architecture).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

export default function FounderMorningDigest() {
  const [digest, setDigest] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setError(null);
    try {
      const [d, l] = await Promise.all([
        axios.get(`${API}/api/founder_dashboard/morning_digest?window_hours=24`, OPTS),
        axios.get(`${API}/api/founder_dashboard/first_light_ledger`, OPTS),
      ]);
      setDigest(d.data);
      setLedger(l.data);
    } catch (err) {
      setError("I couldn't reach the digest just now — try again in a moment.");
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const stampFirstLight = useCallback(async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/founder_dashboard/first_light_ledger/stamp`, {}, OPTS,
      );
      setLedger(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "couldn't stamp just now";
      setError(`Couldn't stamp First Light — ${detail}.`);
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <main data-testid="founder-morning-digest-page" className="founder-digest-page">
      <header className="founder-digest-header">
        <Link to="/" data-testid="founder-digest-back-link" className="founder-digest-back">← Home</Link>
        <h1 data-testid="founder-digest-title">Morning Digest</h1>
        <p className="founder-digest-hint">
          What happened overnight, at a glance.
        </p>
      </header>

      {error && (
        <section className="founder-digest-error" data-testid="founder-digest-error">
          {error}
        </section>
      )}

      {digest && (
        <>
          <section
            className="founder-digest-headline"
            data-testid="founder-digest-headline"
            role="status"
            aria-live="polite"
          >
            {digest.narration_headline}
          </section>

          <div className="founder-digest-grid">
            <DigestCard
              testId="digest-card-overnight-import"
              title="Overnight import"
              stats={[
                { label: "Batches", value: digest.overnight_import.batch_count, testId: "digest-overnight-batches" },
                { label: "Recordings filed", value: digest.overnight_import.imported_count, testId: "digest-overnight-imported" },
                { label: "Duplicates skipped", value: digest.overnight_import.duplicate_count, testId: "digest-overnight-duplicates" },
                { label: "Couldn't accept", value: digest.overnight_import.refused_count, testId: "digest-overnight-refused" },
              ]}
            />
            <DigestCard
              testId="digest-card-evidence"
              title="Evidence preserved"
              stats={[
                { label: "In this window", value: digest.evidence_status.preserved_in_window, testId: "digest-evidence-window" },
                { label: "All-time", value: digest.evidence_status.preserved_total, testId: "digest-evidence-total" },
                { label: "Integrity", value: `${Math.round(digest.evidence_status.integrity_ratio * 100)}%`, testId: "digest-integrity-ratio" },
              ]}
            />
            <DigestCard
              testId="digest-card-analyses"
              title="Lesson analyses"
              stats={[
                { label: "Completed in window", value: digest.lesson_analyses.completed_queue_rows_in_window, testId: "digest-analyses-completed" },
                { label: "Observations added", value: digest.lesson_analyses.observations_in_window, testId: "digest-observations" },
              ]}
            />
            <DigestCard
              testId="digest-card-review-items"
              title="Needs your eye"
              stats={[
                { label: "Awaiting review", value: digest.review_items.queue_awaiting_review, testId: "digest-review-awaiting" },
                { label: "Evidence gaps", value: digest.review_items.evidence_gaps_flagged, testId: "digest-review-gaps" },
                { label: "Total", value: digest.review_items.total, testId: "digest-review-total" },
              ]}
            />
            <DigestCard
              testId="digest-card-queue"
              title="Intelligence queue"
              stats={Object.entries(digest.queue_counts).map(([state, n]) => ({
                label: state.replace(/_/g, " "),
                value: n,
                testId: `digest-queue-${state}`,
              }))}
            />
            <DigestCard
              testId="digest-card-processing"
              title="Processing"
              stats={[
                { label: "Awaiting analysis", value: digest.processing.awaiting_analysis, testId: "digest-proc-awaiting" },
                { label: "Analysing", value: digest.processing.analysing, testId: "digest-proc-analysing" },
                { label: "Complete", value: digest.processing.complete, testId: "digest-proc-complete" },
              ]}
            />
            <DigestCard
              testId="digest-card-duplicates"
              title="Duplicates"
              stats={[
                { label: "Audit entries in window", value: digest.duplicates.audit_entries_in_window, testId: "digest-dupes-audit" },
                { label: "Flagged at enumeration", value: digest.duplicates.enumeration_flagged, testId: "digest-dupes-enum" },
              ]}
            />
            <DigestCard
              testId="digest-card-integrity"
              title="Integrity"
              stats={[
                { label: "Records missing hash", value: digest.integrity_status.records_missing_sha256, testId: "digest-integrity-missing" },
              ]}
            />
            <DigestCard
              testId="digest-card-system-health"
              title="System health"
              stats={[
                { label: "Specialists", value: digest.system_health.specialist_count, testId: "digest-health-specialists" },
                { label: "Manifest", value: digest.system_health.manifest_sha.substring(0, 12) + "…", testId: "digest-health-manifest" },
              ]}
            />
            <DigestCard
              testId="digest-card-failures"
              title="Import failures"
              stats={[
                { label: "Refused files", value: digest.import_failures.length, testId: "digest-failures-count" },
                { label: "Stalled batches", value: digest.stalled_batch_count, testId: "digest-failures-stalled" },
              ]}
            />
          </div>

          <section className="founder-digest-ledger" data-testid="founder-digest-first-light">
            <h2>First Light Ledger</h2>
            <p className="founder-digest-hint" data-testid="founder-digest-first-light-narration">
              {ledger?.narration || ""}
            </p>
            {ledger?.stamped && ledger?.ledger ? (
              <dl className="founder-digest-ledger-body">
                <div><dt>Stamped at</dt><dd data-testid="fl-stamped-at">{new Date(ledger.ledger.stamped_at).toLocaleString()}</dd></div>
                <div><dt>First evidence</dt><dd data-testid="fl-first-evidence">{ledger.ledger.first_evidence.evidence_id}</dd></div>
                <div><dt>Verification</dt><dd data-testid="fl-verification">{ledger.ledger.verification_status}</dd></div>
                <div><dt>Blueprint lineage</dt><dd data-testid="fl-lineage">{Object.keys(ledger.ledger.blueprint_lineage).length} documents pinned</dd></div>
              </dl>
            ) : (
              <button
                type="button"
                className="founder-digest-cta"
                onClick={stampFirstLight}
                disabled={busy || (digest?.evidence_status?.preserved_total || 0) === 0}
                data-testid="founder-digest-stamp-first-light-btn"
              >
                {busy ? "Stamping…" : "Stamp First Light"}
              </button>
            )}
          </section>
        </>
      )}
    </main>
  );
}

function DigestCard({ testId, title, stats }) {
  return (
    <article className="founder-digest-card" data-testid={testId}>
      <h3>{title}</h3>
      <dl>
        {stats.map((s) => (
          <div key={s.label}>
            <dt>{s.label}</dt>
            <dd data-testid={s.testId}>{s.value}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}
